import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  UsersIcon, 
  EditIcon, 
  ShieldIcon, 
  CheckIcon, 
  XIcon, 
  CrownIcon, 
  UserCogIcon,
  TrendingUpIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RolePermissionsSettings = () => {
  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchRoles(),
        fetchAvailablePermissions()
      ]);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      // Use normal roles endpoint and transform data
      const response = await axios.get(`${API}/roles`);
      const rolesWithMetadata = response.data.map(role => ({
        ...role,
        user_count: 0, // Will be populated by backend in future
        total_permissions: 0, // Will be populated by backend in future
        categories_breakdown: {
          modules: 0,
          approval_actions: 0, 
          finance_actions: 0,
          closing_actions: 0,
          data_access: 0,
          administrative: 0,
          reporting: 0
        },
        is_default: role.permissions && role.permissions.includes('*')
      }));
      setRoles(rolesWithMetadata);
    } catch (error) {
      toast.error('Roller yüklenirken hata oluştu');
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const response = await axios.get(`${API}/user-permissions/available`);
      setAvailablePermissions(response.data.permissions);
    } catch (error) {
      toast.error('Yetkiler yüklenirken hata oluştu');
    }
  };

  const fetchRolePermissions = async (roleId) => {
    try {
      const response = await axios.get(`${API}/role-permissions/${roleId}`);
      const permissions = response.data.permissions;
      
      // Convert permissions array to object structure - include ALL 9 categories
      const permissionsByCategory = {
        modules: {},
        approval_actions: {},
        finance_approval_actions: {},
        payment_actions: {},
        closing_actions: {},
        data_access: {},
        administrative: {},
        company_management: {},
        reporting: {}
      };
      
      // Include ALL permissions, both granted and not granted
      permissions.forEach(permission => {
        permissionsByCategory[permission.permission_category][permission.permission_key] = permission.is_granted;
      });
      
      setRolePermissions(permissionsByCategory);
    } catch (error) {
      toast.error('Rol yetkileri yüklenirken hata oluştu');
      setRolePermissions({ modules: {}, approval_actions: {}, finance_approval_actions: {}, payment_actions: {}, closing_actions: {}, data_access: {}, administrative: {}, company_management: {}, reporting: {} });
    }
  };

  const handleOpenPermissionDialog = async (role) => {
    setSelectedRole(role);
    await fetchRolePermissions(role.id);
    setPermissionDialogOpen(true);
  };

  const handlePermissionChange = (category, permissionKey, value) => {
    setRolePermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [permissionKey]: value
      }
    }));
  };

  const handleToggleAllInCategory = (category) => {
    const categoryPermissions = availablePermissions[category] || {};
    const allGranted = Object.keys(categoryPermissions).every(key => 
      rolePermissions[category] && rolePermissions[category][key]
    );
    
    const newCategoryPerms = {};
    Object.keys(categoryPermissions).forEach(key => {
      newCategoryPerms[key] = !allGranted;
    });
    
    setRolePermissions(prev => ({
      ...prev,
      [category]: newCategoryPerms
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/role-permissions/${selectedRole.id}/update`, rolePermissions);
      toast.success(`${selectedRole.name} pozisyonu için yetkiler başarıyla güncellendi`);
      setPermissionDialogOpen(false);
      fetchRoles(); // Refresh roles list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yetkiler güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      toast.error('Pozisyon adı gereklidir');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/roles`, {
        name: newRoleName.trim(),
        permissions: []
      });
      toast.success(`${newRoleName} pozisyonu başarıyla oluşturuldu`);
      setCreateRoleDialogOpen(false);
      setNewRoleName('');
      fetchRoles(); // Refresh roles list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pozisyon oluşturulurken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'modules': return '📱';
      case 'approval_actions': return '✅';
      case 'finance_actions': return '💰';
      case 'closing_actions': return '🔒';
      case 'data_access': return '👁️';
      case 'administrative': return '⚙️';
      case 'reporting': return '📊';
      default: return '🔒';
    }
  };

  const getCategoryTitle = (category) => {
    switch (category) {
      case 'modules': return 'Modül Erişimi';
      case 'approval_actions': return 'Onay Yetkileri';
      case 'finance_actions': return 'Finans ve Ödeme';
      case 'closing_actions': return 'Kapama İşlemleri';
      case 'data_access': return 'Veri Erişim Kapsamı';
      case 'administrative': return 'Yönetimsel Yetkiler';
      case 'reporting': return 'Raporlama Yetkileri';
      default: return category;
    }
  };

  const getCategoryDescription = (category) => {
    switch (category) {
      case 'modules': return 'Hangi sayfalara erişebilir';
      case 'approval_actions': return 'Kimler için onay verebilir';
      case 'finance_actions': return 'Finans onayı ve ödeme yapabilir';
      case 'closing_actions': return 'Avans kapama işlemi yapabilir';
      case 'data_access': return 'Hangi verileri görebilir';
      case 'administrative': return 'Sistem yönetimi yapabilir';
      case 'reporting': return 'Rapor görüntüleme ve oluşturma';
      default: return '';
    }
  };

  const getRoleIcon = (roleName) => {
    if (roleName.includes('Süper Admin') || roleName.includes('Super Admin')) {
      return <CrownIcon className="w-6 h-6 text-yellow-600" />;
    }
    if (roleName.includes('Admin')) {
      return <ShieldIcon className="w-6 h-6 text-red-600" />;
    }
    if (roleName.includes('Yönetici') || roleName.includes('Manager')) {
      return <UserCogIcon className="w-6 h-6 text-blue-600" />;
    }
    return <UsersIcon className="w-6 h-6 text-gray-600" />;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Pozisyon Yetkileri</h2>
          <p className="text-gray-600 mt-2">Yükleniyor...</p>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="w-16 h-16 bg-gray-200 rounded-xl"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2 w-1/4"></div>
                    <div className="h-3 bg-gray-200 rounded mb-3 w-1/2"></div>
                    <div className="flex space-x-6">
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                </div>
                <div className="h-10 bg-gray-200 rounded w-32"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-xl border border-purple-200">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Pozisyon Bazlı Yetki Yönetimi</h2>
            <p className="text-gray-700 text-lg">
              Her pozisyon için standart yetkileri tanımlayın. Bu pozisyona atanan tüm kullanıcılar bu yetkilere sahip olur.
            </p>
          </div>
          <Button
            onClick={() => setCreateRoleDialogOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm font-medium rounded-lg shadow-sm transition-all duration-200 flex items-center space-x-2"
          >
            <UsersIcon className="w-4 h-4" />
            <span>Yeni Pozisyon Oluştur</span>
          </Button>
        </div>
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <TrendingUpIcon className="w-5 h-5 text-green-600" />
            <span className="text-green-700 font-medium">Scalable: Pozisyon = Template</span>
          </div>
          <div className="flex items-center space-x-2">
            <UserCogIcon className="w-5 h-5 text-blue-600" />
            <span className="text-blue-700 font-medium">Kişi bazında override mümkün</span>
          </div>
        </div>
      </div>

      {/* Roles List */}
      <div className="space-y-4">
        {roles.map((role) => (
          <Card key={role.id} className="p-6 hover:shadow-md transition-all duration-200 border-l-4 border-l-purple-500">
            <div className="flex items-center justify-between">
              {/* Role Info Section */}
              <div className="flex items-center space-x-4 flex-1">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm">
                  {getRoleIcon(role.name)}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">{role.name}</h3>
                    {role.is_default && (
                      <Badge variant="secondary" className="bg-purple-100 text-purple-700 px-2 py-1 text-xs">
                        Varsayılan
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{role.user_count} kullanıcı bu pozisyonda</p>
                  
                  {/* Permission Summary */}
                  <div className="grid grid-cols-4 gap-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-200">
                    <div className="text-center">
                      <div className="text-lg mb-1">📱</div>
                      <div className="text-xs text-gray-600 font-medium mb-1">Modüller</div>
                      <div className="text-sm font-bold text-gray-900">{role.categories_breakdown.modules || 0}</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-lg mb-1">✅</div>
                      <div className="text-xs text-gray-600 font-medium mb-1">Onaylar</div>
                      <div className="text-sm font-bold text-gray-900">{role.categories_breakdown.approval_actions || 0}</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-lg mb-1">💰</div>
                      <div className="text-xs text-gray-600 font-medium mb-1">Finans</div>
                      <div className="text-sm font-bold text-gray-900">{role.categories_breakdown.finance_actions || 0}</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="text-lg mb-1">🔒</div>
                      <div className="text-xs text-gray-600 font-medium mb-1">Toplam</div>
                      <div className="text-sm font-bold text-gray-900">{role.total_permissions}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <Button
                onClick={() => handleOpenPermissionDialog(role)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-3 text-sm font-medium rounded-lg shadow-sm transition-all duration-200 flex items-center space-x-2 ml-6"
              >
                <EditIcon className="w-4 h-4" />
                <span>Pozisyon Yetkilerini Düzenle</span>
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {roles.length === 0 && (
        <Card className="p-12 text-center">
          <ShieldIcon className="w-20 h-20 text-gray-300 mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Henüz pozisyon bulunmuyor</h3>
          <p className="text-gray-600 text-lg">Pozisyon yetkileri tanımlamak için önce roller oluşturulmalı.</p>
        </Card>
      )}

      {/* Permission Management Dialog */}
      <Dialog open={permissionDialogOpen} onOpenChange={setPermissionDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                {selectedRole && getRoleIcon(selectedRole.name)}
              </div>
              <span>
                {selectedRole?.name} - Pozisyon Yetkileri
              </span>
            </DialogTitle>
          </DialogHeader>

          {selectedRole && (
            <div className="space-y-6">
              {/* Role Info */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    {getRoleIcon(selectedRole.name)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">{selectedRole.name} Pozisyonu</h4>
                    <p className="text-sm text-gray-700">
                      Bu pozisyondaki {selectedRole.user_count} kullanıcı, belirlediğiniz yetkilere sahip olacak
                    </p>
                    {selectedRole.is_default && (
                      <Badge variant="outline" className="text-xs mt-1 border-purple-300 text-purple-700">
                        Varsayılan Pozisyon
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Permissions by Category */}
              {Object.entries(availablePermissions).map(([category, permissions]) => (
                <Card key={category} className="p-6 border-l-4 border-l-purple-500">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <span className="text-2xl">{getCategoryIcon(category)}</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {getCategoryTitle(category)}
                        </h3>
                        <p className="text-sm text-gray-600">{getCategoryDescription(category)}</p>
                        <Badge variant="secondary" className="text-xs mt-1">
                          {Object.keys(rolePermissions[category] || {}).filter(key => 
                            rolePermissions[category][key]
                          ).length} / {Object.keys(permissions).length} aktif
                        </Badge>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleAllInCategory(category)}
                      className="text-sm"
                    >
                      Tümünü Değiştir
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {Object.entries(permissions).map(([permissionKey, description]) => {
                      const parts = description.split(' - ');
                      const title = parts[0];
                      const subtitle = parts[1];
                      
                      return (
                        <div
                          key={permissionKey}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border border-gray-200"
                        >
                          <div className="flex-1">
                            <div className="font-semibold text-sm text-gray-900 mb-1">
                              {title}
                            </div>
                            <div className="text-sm text-gray-600">
                              {subtitle}
                            </div>
                            <div className="text-xs text-gray-400 mt-1 font-mono">
                              {permissionKey}
                            </div>
                          </div>
                          <div className="ml-4">
                            <Button
                              variant={rolePermissions[category] && rolePermissions[category][permissionKey] ? "default" : "outline"}
                              size="sm"
                              onClick={() => handlePermissionChange(
                                category, 
                                permissionKey, 
                                !(rolePermissions[category] && rolePermissions[category][permissionKey])
                              )}
                              className={`${
                                rolePermissions[category] && rolePermissions[category][permissionKey]
                                  ? 'bg-green-600 hover:bg-green-700 text-white'
                                  : 'border-gray-300 hover:border-gray-400 text-gray-600'
                              }`}
                            >
                              {rolePermissions[category] && rolePermissions[category][permissionKey] ? (
                                <>
                                  <CheckIcon className="w-4 h-4 mr-1" />
                                  Aktif
                                </>
                              ) : (
                                <>
                                  <XIcon className="w-4 h-4 mr-1" />
                                  Pasif
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setPermissionDialogOpen(false)}
                  disabled={saving}
                >
                  İptal
                </Button>
                <Button
                  onClick={handleSavePermissions}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {saving ? 'Kaydediliyor...' : 'Pozisyon Yetkilerini Kaydet'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog open={createRoleDialogOpen} onOpenChange={setCreateRoleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <UsersIcon className="w-5 h-5 text-green-600" />
              </div>
              <span>Yeni Pozisyon Oluştur</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pozisyon Adı
              </label>
              <Input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="Örn: İnsan Kaynakları, Satış Müdürü"
                className="w-full"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateRole();
                  }
                }}
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Not:</strong> Pozisyon oluşturulduktan sonra yetkileri tanımlayabileceksiniz. 
                Varsayılan olarak hiçbir yetkiye sahip olmayacaktır.
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setCreateRoleDialogOpen(false);
                  setNewRoleName('');
                }}
                disabled={saving}
              >
                İptal
              </Button>
              <Button
                onClick={handleCreateRole}
                disabled={saving || !newRoleName.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? 'Oluşturuluyor...' : 'Pozisyon Oluştur'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolePermissionsSettings;